import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from "@mui/x-data-grid";
import {
  Box,
  Button,
  FormControl,
  Typography,
  TextField,
  InputAdornment,
} from "@mui/material";
import { green, blue, red } from "@mui/material/colors";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import SyncIcon from "@mui/icons-material/Sync";
import SearchIcon from "@mui/icons-material/Search";

import { Delete, Get, Put } from "../../Common/WebRequest";
import { getValue, getStorageItem } from "../../Common/Utility";
import { handleExport } from "./RowsExport";

import columnsBuilderDetails from "./columnsBuilderDetails";
import columnsBuilder from "./columnsBuilder";
import { DownloadPdf } from "../../Common/WebRequestPdf";
import DeleteConfirmation from "../../components/DeleteConfirmation";
import DropDownRegion from "../../components/DropDown/Region/DropDownRegion";
import DropDownBranch from "../../components/DropDown/Branch/DropDownBranch";

import { WebRequestPdfViewer } from "../../Common/WebRequestPdfViewer";

export default function StudentCalculationTable() {
  const [calcData, setCalcData] = useState([]);
  const [regionData, setRegionData] = useState([]);

  const [detailsData, setDetailsData] = useState([]);
  const [regionId, setRegionId] = useState(0);
  const [branchId, setBranchId] = useState(0);
  const [studentId, setStudentId] = useState(0);
  const [studentName, setStudentName] = useState("");
  const [rechnungsnummer, setRechnungsnummer] = useState("-");
  const [reload, setReload] = useState(false);
  const [error, setError] = useState("");
  const [sumOfAmounts, setSumOfAmounts] = useState(0);
  const [sumOfPayedAmounts, setSumOfPayedAmounts] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedRows, setSelectedRows] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    (async () => {
      const admin = await getStorageItem("login", "isAdmin");
      setIsAdmin(!!admin);
    })();
  }, []);

  let withAll = true; // @ALL   --> Region:@All , Branch:@All
  const gridContainerRef = useRef(null);
  // Delete Confirmation

  const [displayConfirmationModal, setDisplayConfirmationModal] =
    useState(false);
  const [deleteMessage, setDeleteMessage] = useState(null);

  // Custom search function for the DataGrid
  const customSearchFunction = (searchValue) => {
    if (!searchValue) return calcData;

    return calcData.filter((row) => {
      // Check if any field contains the search value
      return Object.keys(row).some((key) => {
        const value = row[key];

        // Special handling for date fields
        if (
          key === "birthDate" ||
          key === "pdfDate" ||
          key === "entryDateFrom" ||
          key === "entryDateTo"
        ) {
          const dateValue = value ? String(value) : "";
          return dateValue.toLowerCase().includes(searchValue.toLowerCase());
        }

        // For other fields, use the default contains behavior
        return String(value).toLowerCase().includes(searchValue.toLowerCase());
      });
    });
  };

  // State for the search value
  const [searchValue, setSearchValue] = useState("");

  // Ref for the search input field
  const searchInputRef = useRef(null);

  // Filtered data based on search value
  const filteredData = searchValue
    ? customSearchFunction(searchValue)
    : calcData;

  const [filterModel, setFilterModel] = useState({ items: [] });

  function applyGridFilter(rows, filterModel) {
    if (!filterModel?.items?.length) return rows;
    return rows.filter((row) => {
      return filterModel.items.some((filterItem) => {
        const { field, value } = filterItem;
        if (value == null || value === "") return true;
        if (field === "isPartner") {
          if (value === "true" || value === true) return row[field] === true;
          if (value === "false" || value === false) return row[field] === false;
          return true;
        }
        if (
          field === "birthDate" ||
          field === "pdfDate" ||
          field === "entryDateFrom" ||
          field === "entryDateTo"
        ) {
          const dateValue = row[field] ? String(row[field]) : "";
          return dateValue.toLowerCase().includes(String(value).toLowerCase());
        }
        return String(row[field] ?? "")
          .toLowerCase()
          .includes(String(value).toLowerCase());
      });
    });
  }

  const gridFilteredData = useMemo(
    () => applyGridFilter(filteredData, filterModel),
    [filteredData, filterModel]
  );

  // Effect to maintain focus on the search input field
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchValue]);

  useEffect(() => {
    (async () => {
      setIsLoading(true); // Start loading
      try {
        if (studentId === 0) {
          let regions = await Get("region");
          setRegionData(regions.result);
          if (regionId === 0) {
            setRegionId(-1);
            setBranchId(-1);
          }
        }
        if (regionId === 0 || branchId === 0) {
          setIsLoading(false);
          return;
        }
        if (studentId === 0) {
          let res = await Get(
            "Calculation/Summary/" + regionId + "/" + branchId
          );

          if (res.isSuccess) {
            setCalcData(res.result.calculationsSummaries);
            setSumOfAmounts(getSumOfAmount(res.result.calculationsSummaries));
            setSumOfPayedAmounts(
              getSumOfPayedAmount(res.result.calculationsSummaries)
            );
          } else {
            setSumOfAmounts(0);
            setSumOfPayedAmounts(0);
            if (!res.Error.includes("No Results found"))
              setError("Error: " + res.Status + "/" + res.Error);
          }
        }
        if (studentId === 0) {
          setIsLoading(false);
          return;
        }
        let details = await Get(
          "Calculation/Details/" + branchId + "/" + rechnungsnummer
        );
        if (details.isSuccess) {
          setDetailsData(details.result);
        }
        if (gridContainerRef.current) {
          gridContainerRef.current.focus();
        }
      } catch (error) {
        setError("Error loading data: " + error.message);
      } finally {
        setIsLoading(false); // End loading regardless of success/failure
      }
    })();
  }, [regionId, branchId, rechnungsnummer, reload, studentId]);

  function getNextId(rechnungsnummer) {
    const srows = [...calcData].sort(
      (a, b) => a.rechnungsnummer - b.rechnungsnummer
    );
    const index = srows.findIndex(
      (row) => row.rechnungsnummer === rechnungsnummer
    );
    if (index < calcData.length - 1) return index + 1;
    return index;
  }

  function getPrevId(rechnungsnummer) {
    const srows = [...calcData].sort(
      (a, b) => a.rechnungsnummer - b.rechnungsnummer
    );
    const index = srows.findIndex(
      (row) => row.rechnungsnummer === rechnungsnummer
    );
    if (index > 0) return index - 1;
    return index;
  }
  const handleCellKeyDown = (params, event) => {
    if (event.key === "ArrowDown") {
      let nextId = getNextId(params.row.rechnungsnummer);
      setRechnungsnummer(calcData[nextId].rechnungsnummer);
      setStudentName(calcData[nextId].studentName);
      console.log(calcData[nextId].rechnungsnummer);
    } else if (event.key === "ArrowUp") {
      let prevId = getPrevId(params.row.rechnungsnummer);
      console.log(calcData[prevId].rechnungsnummer);
      setRechnungsnummer(calcData[prevId].rechnungsnummer);
      setStudentName(calcData[prevId].studentName);
    }
  };

  async function handleRowClick(params, event) {
    //event.defaultMuiPrevented = true;
    if (params.row) {
      //setBranchId(params.row.branchId);
      setStudentId(params.row.studentId);
      setRechnungsnummer(params.row.rechnungsnummer);
      setStudentName(params.row.studentName);
    }
  }
  function handleRefresh() {
    setCalcData([]);
    setDetailsData([]);
    //setBranchId(0);
    setStudentId(0);
    setRechnungsnummer("");
    setStudentName("");
    setReload(!reload);
  }
  const handleAfterDeleteClick = () => {
    setDisplayConfirmationModal(false);
    doDeleteApi();
    setDetailsData([]);
    setStudentName("");
  };

  const hideConfirmationModal = () => {
    setDisplayConfirmationModal(false);
  };

  function handleExportClick(format) {
    handleExport(filteredData, regionData, format);
  }

  function DefinedToolbar() {
    return (
      <Box
        sx={{
          p: 0.5,
          pb: 0,
        }}
      >
        <GridToolbarContainer>
          <Button
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadClick}
            disabled={selectedRows.length === 0}
          >
            Download
          </Button>
          <Button
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
            disabled={selectedRows.length === 0}
          >
            Delete
          </Button>

          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarDensitySelector>
            <GridToolbarDensitySelector />
          </GridToolbarDensitySelector>
          <Button
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={() => handleExportClick("csv")}
          >
            Export CSV
          </Button>
          <Button
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={() => handleExportClick("pdf")}
            sx={{ ml: 1 }}
          >
            Export PDF
          </Button>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            inputRef={searchInputRef}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ width: 200, ml: 1 }}
          />
          <Button
            color="primary"
            startIcon={<SyncIcon />}
            onClick={handleRefresh}
            sx={{ ml: 1 }}
          >
            Refresh
          </Button>
        </GridToolbarContainer>
      </Box>
    );
  }
  function onRowsSelectionHandler(ids) {
    const selectedRowsData = ids.map((id) =>
      calcData.find((row) => row.rechnungsnummer === id)
    );
    setSelectedRows(selectedRowsData);
    if (selectedRowsData.length > 0) {
      setSumOfAmounts(getSumOfAmount(selectedRowsData));
      setSumOfPayedAmounts(getSumOfPayedAmount(selectedRowsData));
    } else {
      setSumOfAmounts(getSumOfAmount(calcData));
      setSumOfPayedAmounts(getSumOfPayedAmount(calcData));
    }
  }
  async function handleDeleteClick() {
    if (selectedRows.length > 0) {
      setDeleteMessage(`Möchten Sie wirklich löschen?`);
      setDisplayConfirmationModal(true);
    }
  }
  async function doDeleteApi() {
    if (selectedRows.length > 0) {
      let i = 0;
      let AllRechnungsnummer = [];
      for (i = 0; i < selectedRows.length; i++) {
        AllRechnungsnummer.push(selectedRows[i].rechnungsnummer);
      }

      /* AllRechnungsnummer = AllRechnungsnummer.toString().replace(
        new RegExp(",", "g"),
        ";"
      );*/
      const requestBody = {
        rechnungsnummern: AllRechnungsnummer,
      };
      if (AllRechnungsnummer) {
        let res = await Delete("Calculation/DeletePdf", "", requestBody);
        if (res.isSuccess) {
          let filteredData = calcData.filter((v) => {
            return !AllRechnungsnummer.includes(v.rechnungsnummer);
          });
          setCalcData(filteredData);
        } else {
          if (!res.Error.includes("No Results found")) {
            setError("Error: " + res.Error);
          }
        }
      }
    }
  }

  async function handleDownloadClick() {
    try {
      for (const row of selectedRows) {
        const filePath = row.pdfFileName;
        if (!filePath) continue;
        const but = String(row.butNumber ?? "").trim();
        const downloadName = `Abrechnung_${but || "NA"}_${filePath}`;
        await DownloadPdf("Calculation/download/" + filePath, downloadName);
      }
    } catch (error) {
      console.error("Error fetching PDFs:", error);
      throw error;
    }
  }
  async function handleOnCellClick(params) {
    //apiRef.current.selectRow(params.rechnungsnummer);
    if (params.field === "pdfFileName") {
      let fileName = params.value;
      if (fileName.length > 0) {
        let props = { path: "Calculation/pdf/" + fileName };
        await WebRequestPdfViewer(props);
      }
    } else if (params.field === "isPayed") {
      if (!isAdmin) return;

      const updatedRows = calcData.map((row) =>
        row.rechnungsnummer === params.id
          ? { ...row, isPayed: !row.isPayed }
          : row
      );
      setCalcData(updatedRows);

      const foundUpdatedRow = updatedRows.find(
        (row) => row.rechnungsnummer === params.id
      );

      setSumOfPayedAmounts(getSumOfPayedAmount(selectedRows));
      // Update DB Calculations.IsPayed

      await handleUpdateIsPayed(params.id, foundUpdatedRow.isPayed);

      const indexToUpdate = selectedRows.findIndex(
        (row) => row.rechnungsnummer === params.id
      );
      if (selectedRows.length !== 0) {
        if (indexToUpdate !== -1) selectedRows[indexToUpdate] = foundUpdatedRow;

        setSumOfPayedAmounts(getSumOfPayedAmount(selectedRows));
      } else {
        setSumOfPayedAmounts(getSumOfPayedAmount(updatedRows));
      }
    }
  }

  async function handleUpdateIsPayed(rechnungsNummer, isPayed) {
    let payedRechnung = {
      rechnungsNummer: rechnungsNummer,
      isPayed: isPayed,
    };
    let res = await Put("Calculation/UpdatePayedRechnung", "", payedRechnung);

    if (!res.isSuccess) {
      setError(res.Error);
    }
  }
  function handleRegionValue(e) {
    setRegionId((currentRegionId) => {
      return getValue(e.target.value);
    });
    //setStudentName("");
    if (withAll) {
      //-1  is : @All
      setBranchId(-1);
    }
    setStudentId(0);
    setRechnungsnummer("");
    setStudentName("");
    setCalcData([]);
    setDetailsData([]);
    setError("");
  }
  function handleBranchValue(e) {
    setBranchId((currentBranchId) => {
      return getValue(e.target.value);
    });
    //setStudentName("");
    console.log("Selected Branch = ", branchId);
    setStudentId(0);
    setRechnungsnummer("");
    setStudentName("");
    setCalcData([]);
    setDetailsData([]);
    setError("");
  }
  function handleOnBranchPopulate(branchIds) {
    if (branchIds.length > 0) {
      setBranchId(branchIds[0]);
      console.log("handleOnPopulate: ", branchIds);
    }
    //setBranchesIds(branches);
  }
  function getSumOfDetails() {
    const sum = detailsData.reduce((accumulator, object) => {
      return accumulator + object.amount;
    }, 0);
    return sum;
  }
  const getSumOfAmount = (data) => {
    const sum = data.reduce((accumulator, object) => {
      return accumulator + object.sumOfAmount;
    }, 0);
    return sum.toLocaleString("en") + " €";
  };

  const getSumOfPayedAmount = (data) => {
    const sum = data
      .filter((object) => object.isPayed)
      .reduce((accumulator, object) => {
        return accumulator + object.sumOfAmount;
      }, 0);

    return sum.toLocaleString("en") + " €";
  };

  return (
    <>
      <br />
      {/* <Button variant="contained" onClick={handleExportToCsv}>
        Export to CSV
      </Button> */}
      <Box display="flex" flexDirection="row" alignItems="center">
        <Box>
          <Typography
            variant="headline"
            component="h3"
            align="left"
            paddingLeft={"10px"}
            color={blue[500]}
          >
            Abrechnungsübersicht
          </Typography>
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          sx={{ marginRight: 2 }}
        >
          <Typography
            variant="subtitle1"
            color={blue[500]}
            fontWeight="Bold"
            sx={{ marginLeft: 7, marginRight: 2 }}
          >
            Region
          </Typography>
          <FormControl>
            <DropDownRegion
              regionID={regionId}
              onChange={handleRegionValue}
              withAll={withAll}
              // onPopulate={handleOnPopulate}
            />
          </FormControl>
        </Box>
        <Box display="flex" flexDirection="row" alignItems="center">
          <Typography
            variant="subtitle1"
            color={blue[500]}
            fontWeight="Bold"
            sx={{ marginRight: 2 }}
          >
            Abrechnungstelle
          </Typography>
          <FormControl>
            <DropDownBranch
              regionID={regionId}
              branchID={branchId}
              onChange={handleBranchValue}
              onPopulate={handleOnBranchPopulate}
              withAll={withAll}
            />
          </FormControl>
        </Box>
      </Box>
      <br />
      <Typography
        variant="caption"
        component="h6"
        align="left"
        color={red.A400}
      >
        {error}
      </Typography>

      <div
        style={{ display: "flex", justifyContent: "left", paddingLeft: "20px" }}
      >
        <Typography
          variant="headline"
          component="h4"
          align="center"
          color={green[800]}
          style={{ marginRight: "20px" }}
        >
          Gesamt Bezahlt = {sumOfPayedAmounts}
        </Typography>
        <Typography
          variant="headline"
          component="h4"
          align="center"
          color={green.A700}
        >
          Gesamt = {sumOfAmounts}
        </Typography>
      </div>
      <Box
        sx={{
          width: "100%",
          height: "500px",
          "& .Mui-even": {
            backgroundColor: "#f3f7ff",
          },
          "& .Mui-odd": {
            backgroundColor: "#EEEEEE",
          },
          "& .MuiDataGrid-cell:hover": {
            backgroundColor: "#80b3ff",
          },
          "&& .MuiDataGrid-row:hover": {
            backgroundColor: "#cce0ff",
            /*  "@media (hover: none)": {
              backgroundColor: "transparent",
            },*/
            // color: "red"
          },
          "& .color--red--header": {
            backgroundColor: "rgba(255, 7, 0, 0.55)",
          },
          "& .color--green--header": {
            backgroundColor: "rgba(142, 200, 144, 0.91)",
          },
          "& .super-app.negative": {
            backgroundColor: "rgba(157, 255, 118, 0.49)",
            color: "#ff7773",
            fontWeight: "600",
          },
          "& .super-app.positive": {
            backgroundColor: "rgba(207, 255, 188, 0.48)",
            // backgroundColor: "#d47483",
            color: "#00c853",
            fontWeight: "600",
          },
          "& .super-app.notEmpty": {
            backgroundColor: "rgba(255, 119, 115, 0.19)",
            // backgroundColor: "#d47483",
            color: "#4A148C",
            fontWeight: "600",
          },
        }}
      >
        <DataGrid
          // apiRef={apiRef}
          checkboxSelection
          disableRowSelectionOnClick
          editable={true}
          editMode="cell"
          onRowSelectionModelChange={(ids) => onRowsSelectionHandler(ids)}
          onRowClick={handleRowClick}
          onCellClick={handleOnCellClick}
          onCellKeyDown={handleCellKeyDown}
          initialState={{
            ...calcData.initialState,
            columns: {
              ...calcData.initialState?.columns,
              columnVisibilityModel: {
                studentId: false,
                branchId: false,
              },
            },
          }}
          slots={{ toolbar: DefinedToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              exportOptions: {
                exportAll: true,
                exportSelected: true,
                exportHiddenColumns: true,
              },
            },
          }}
          density="compact"
          rows={filteredData}
          columns={columnsBuilder({ regionData, isAdmin })}
          getRowId={(row) => row.rechnungsnummer}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd"
          }
          loading={isLoading}
          filterMode="client"
          filterModel={filterModel}
          onFilterModelChange={(newModel) => setFilterModel(newModel)}
          getFilteredRowModel={(params) => {
            const { rows, filterModel: model } = params;
            return applyGridFilter(rows, model);
          }}
          exportOptions={{
            fields: [
              "studentId",
              "branchId",
              "isPayed",
              "studentName",
              "regionId",
              "period",
              "sumOfAmount",
              "pdfDate",
              "birthDate",
              "butNumber",
              "rechnungsnummer",
              "pdfFileName",
              "isPartner",
              "entryDateFrom",
              "entryDateTo",
            ],
            getRowsToExport: () => filteredData,
          }}
        />

        <Box
          sx={{
            mt: 1,
            mb: 1,
            pt: 1.5,
            pb: 1.5,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
            Summen (gefiltert)
          </Typography>
          <Typography component="span" sx={{ color: green[700], fontWeight: 500 }}>
            Summe bezahlt:{" "}
            {gridFilteredData
              .filter((r) => r.isPayed)
              .reduce((acc, r) => acc + (Number(r.sumOfAmount) || 0), 0)
              .toLocaleString("en")}{" "}
            €
          </Typography>
          <Typography
            component="span"
            sx={{ color: green[700], fontWeight: 500, ml: 2 }}
          >
            Summe Betrag:{" "}
            {gridFilteredData
              .reduce((acc, r) => acc + (Number(r.sumOfAmount) || 0), 0)
              .toLocaleString("en")}{" "}
            €
          </Typography>
        </Box>

        <Box
          sx={{
            width: "100%",
            height: "250px",
          }}
        >
          <Box display="flex" alignItems="center">
            <Typography
              variant="h5"
              bgcolor={blue[500]}
              color={"white"}
              fontWeight="bold"
            >
              {studentName}
            </Typography>
            <Box sx={{ flexGrow: 1 }}>
              <Typography
                align="center"
                variant="h5"
                color={green.A700}
                fontWeight="bold"
              >
                Summe = {getSumOfDetails().toLocaleString("en")} €
              </Typography>
            </Box>
          </Box>
          <DataGrid
            hideFooterPagination={true}
            density="compact"
            rowSelection={false}
            rows={detailsData}
            columns={columnsBuilderDetails()}
            getRowId={(row) => row.id}
            rowsPerPageOptions={100}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 100, page: 1 },
              },
            }}
          />
        </Box>
      </Box>
      <DeleteConfirmation
        showModal={displayConfirmationModal}
        confirmModal={handleAfterDeleteClick}
        hideModal={hideConfirmationModal}
        message={deleteMessage}
      />
    </>
  );
}
